import React, { useState } from "react";
import { Plus, Pencil, Trash2, Database, X } from "lucide-react";
import { usePipelineConfigs } from "hooks/usePipelineConfigs";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useToast } from "components/Toast";
import Skeleton from "components/Skeleton";
import ConfirmDialog from "components/ConfirmDialog";

export default function PipelineConfigTab() {
  const { user } = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { configs, loading, refetch } = usePipelineConfigs();
  const { toast } = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // config doc being edited
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", description: "", onConfirm: () => {} });

  const [form, setForm] = useState({
    companyId: "",
    slug: "",
    integrationName: "",
    lastNHourToProcess: 10,
    refreshHoursBack: 24,
    isActive: true,
  });

  const resetForm = () => {
    setForm({
      companyId: "",
      slug: "",
      integrationName: "",
      lastNHourToProcess: 10,
      refreshHoursBack: 24,
      isActive: true,
    });
    setEditing(null);
    setShowForm(false);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (config) => {
    setEditing(config);
    setForm({
      companyId: config.companyId || "",
      slug: config.slug || "",
      integrationName: config.integrationName || "",
      lastNHourToProcess: config.lastNHourToProcess ?? 10,
      refreshHoursBack: config.refreshHoursBack ?? 24,
      isActive: config.isActive ?? true,
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!editing) {
      // Create
      if (!form.companyId || !form.slug || !form.integrationName) {
        toast({
          status: "destructive",
          title: "Please fill in companyId, slug, and integration name",
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      if (editing) {
        await makeApiRequest(
          "put",
          apiRoutes.updatePipelineConfig(editing._id),
          { id: user.id, accessToken: user.accessToken },
          effectiveCompanyId,
          {
            integrationName: form.integrationName,
            lastNHourToProcess: Number(form.lastNHourToProcess),
            refreshHoursBack: Number(form.refreshHoursBack),
            isActive: form.isActive,
          }
        );
        toast({ status: "success", title: "Pipeline config updated" });
      } else {
        await makeApiRequest(
          "post",
          apiRoutes.createPipelineConfig,
          { id: user.id, accessToken: user.accessToken },
          effectiveCompanyId,
          {
            companyId: form.companyId,
            slug: form.slug,
            integrationName: form.integrationName,
            lastNHourToProcess: Number(form.lastNHourToProcess),
            refreshHoursBack: Number(form.refreshHoursBack),
          }
        );
        toast({ status: "success", title: "Pipeline config created" });
      }
      resetForm();
      refetch();
    } catch (err) {
      toast({
        status: "destructive",
        title: editing
          ? "Failed to update pipeline config"
          : "Failed to create pipeline config",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = (config) => {
    setConfirmDialog({
      open: true,
      title: "Deactivate Pipeline Config",
      description: `Deactivate pipeline config for ${config.slug} / ${config.integrationName}?`,
      confirmLabel: "Deactivate",
      variant: "destructive",
      onConfirm: async () => {
        setConfirmDialog((c) => ({ ...c, open: false }));
        try {
          await makeApiRequest(
            "delete",
            apiRoutes.deletePipelineConfig(config._id),
            { id: user.id, accessToken: user.accessToken },
            effectiveCompanyId
          );
          toast({ status: "success", title: "Pipeline config deactivated" });
          refetch();
        } catch (err) {
          toast({
            status: "destructive",
            title: "Failed to deactivate pipeline config",
            description: err.response?.data?.error || "Unknown error",
          });
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton.Rect className="h-10 w-full" />
        <Skeleton.Rect className="h-10 w-full" />
        <Skeleton.Rect className="h-10 w-2/3" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {configs.length} pipeline config{configs.length !== 1 ? "s" : ""}
        </h3>
        <button className="btn btn-primary" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          Add Config
        </button>
      </div>

      {/* Table */}
      {configs.length > 0 ? (
        <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Integration</th>
                <th className="px-4 py-3 font-medium">Hours to Process</th>
                <th className="px-4 py-3 font-medium">Refresh Hours</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((config) => (
                <tr
                  key={config._id}
                  className="border-b border-border last:border-0"
                >
                  <td className="px-4 py-3 font-mono text-xs">
                    {config.slug}
                  </td>
                  <td className="px-4 py-3">{config.integrationName}</td>
                  <td className="px-4 py-3 text-center">
                    {config.lastNHourToProcess}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {config.refreshHoursBack}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        config.isActive
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      }`}
                    >
                      {config.isActive ? "active" : "inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        className="btn btn-ghost btn-xs"
                        onClick={() => openEdit(config)}
                        title="Edit config"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {config.isActive && (
                        <button
                          className="btn btn-ghost btn-xs text-destructive"
                          onClick={() => handleDeactivate(config)}
                          title="Deactivate config"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="surface rounded-xl ring-1 ring-border p-8 text-center">
          <Database className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            No pipeline configs yet. Create one to get started.
          </p>
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-md shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editing ? "Edit Pipeline Config" : "Add Pipeline Config"}
              </h3>
              <button className="btn btn-ghost btn-xs" onClick={resetForm}>
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              {!editing && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1">
                      Company ID
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      placeholder="UUID"
                      value={form.companyId}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, companyId: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1">
                      Slug
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      placeholder="e.g. konfyt"
                      value={form.slug}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, slug: e.target.value }))
                      }
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-foreground block mb-1">
                  Integration Name
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="e.g. checkoutchamp"
                  value={form.integrationName}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      integrationName: e.target.value,
                    }))
                  }
                  list="integration-presets"
                />
                <datalist id="integration-presets">
                  <option value="checkoutchamp" />
                  <option value="phoenix" />
                </datalist>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">
                    Hours to Process
                  </label>
                  <input
                    type="number"
                    className="input input-bordered w-full"
                    min={1}
                    value={form.lastNHourToProcess}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        lastNHourToProcess: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">
                    Refresh Hours Back
                  </label>
                  <input
                    type="number"
                    className="input input-bordered w-full"
                    min={1}
                    value={form.refreshHoursBack}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        refreshHoursBack: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {editing && (
                <div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, isActive: e.target.checked }))
                      }
                      className="rounded border-border"
                    />
                    <span className="font-medium">Active</span>
                  </label>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  className="btn btn-ghost flex-1"
                  onClick={resetForm}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary flex-1"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting
                    ? "Saving..."
                    : editing
                      ? "Save Changes"
                      : "Create Config"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        {...confirmDialog}
        onCancel={() => setConfirmDialog((c) => ({ ...c, open: false }))}
      />
    </div>
  );
}
